import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  FiltersControls,
  Pagination,
  Spinner,
  Container,
  Filters,
  Heading,
  Row,
  Col,
  Dropdown,
  BoxList,
} from 'components';
import PropTypes from 'prop-types';
import {
  loadListCreator,
  setListPageCreator,
  setListPerPageCreator,
  setListSortCreator,
  setListFiltersCreator,
  removeListFiltersCreator,
  loadGlobalCountriesCreator,
} from 'store/actionsCreators';
import { Padding, Margin } from 'styled-components-spacing';
import { isLoading, globalPropTypes } from 'helpers';

const sortByConfig = {
  name: 'sortBy',
  options: [
    { label: 'Name: descending', value: 'name.desc' },
    { label: 'Name: ascending', value: 'name.asc' },
  ],
};

const Certificates = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  countries,
  loadGlobalCountries,
}) => {
  const { filters, items, page, pages, perPage, sort } = list;
  const { t } = useTranslation();
  const scrollAfterFiltersRef = useRef(null);

  const filtersFields = [
    {
      type: 'input',
      name: 'name',
      id: 'f-name',
      label: t('page.user.cis.certificationBodiesGlobal.name'),
      placeholder: t('page.user.cis.certificationBodiesGlobal.namePlaceholder'),
    },
    {
      type: 'input',
      name: 'initials',
      id: 'f-initials',
      label: t('page.user.cis.certificationBodiesGlobal.initials'),
      placeholder: t('page.user.cis.certificationBodiesGlobal.initialsPlaceholder'),
    },
    {
      type: 'input',
      name: 'email',
      id: 'f-email',
      label: t('page.user.cis.certificationBodiesGlobal.email'),
      placeholder: t('page.user.cis.certificationBodiesGlobal.emailPlaceholder'),
    },
    {
      type: 'select',
      name: 'country',
      id: 'f-country',
      label: t('page.user.cis.certificationBodiesGlobal.country'),
      options: countries,
      placeholder: t('page.user.cis.certificationBodiesGlobal.countryPlaceholder'),
    },
    {
      type: 'input',
      name: 'telephone',
      id: 'f-telephone',
      label: t('page.user.cis.certificationBodiesGlobal.telephone'),
      placeholder: t('page.user.cis.certificationBodiesGlobal.telephonePlaceholder'),
    },
    {
      type: 'input',
      name: 'website',
      id: 'f-website',
      label: t('page.user.cis.certificationBodiesGlobal.website'),
      placeholder: t('page.user.cis.certificationBodiesGlobal.websitePlaceholder'),
    },
  ];

  useEffect(() => {
    if (!countries.length) loadGlobalCountries();
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  return (
    <PageContent>
      <Container>
        <Row>
          <Col size={{ xl: 1 / 3 }}>
            <Padding all={{ xs: 12, xl: 20 }} className="border-xs">
              <Dropdown
                openbreakpoint="xl"
                title={
                  <Heading as="h3" size="h4">
                    {t('page.user.cis.certificationBodiesGlobal.filters')}
                  </Heading>
                }
              >
                <Filters
                  fields={filtersFields}
                  filters={filters}
                  onChangeFilter={(payload) => setFilters(payload)}
                  onClickApplyFilters={() => {
                    setPage(1);
                    loadList();
                  }}
                  onClickRemoveAllFilters={() => removeFilters()}
                  scrollAfterFiltersRef={scrollAfterFiltersRef}
                />
              </Dropdown>
            </Padding>
          </Col>
          <Margin as={Col} size={{ xl: 2 / 3 }} top={{ xs: 16, xl: 0 }}>
            <FiltersControls
              perPageSelected={perPage}
              perPageOnChange={(payload) => setPerPage(payload.value)}
              sortBySelected={sort}
              sortByOnChange={(payload) => setSort(payload.value)}
              sortByConfig={sortByConfig}
            />
            {loading ? <Spinner /> : null}
            {!loading ? (
              <div ref={scrollAfterFiltersRef}>
                <BoxList
                  type="certification-body"
                  items={items}
                  alert={t('global.noResultsFound')}
                  headerAs="h2"
                />
                <Pagination
                  styled
                  currentPage={page}
                  totalPages={pages}
                  onChange={(payload) => {
                    setPage(payload);
                    loadList();
                  }}
                />
              </div>
            ) : null}
          </Margin>
        </Row>
      </Container>
    </PageContent>
  );
};

Certificates.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
};

Certificates.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, list, globals }) => ({
  loading: isLoading(loading, 'LOAD_LIST_CERTIFICATION_BODIES'),
  list: list.certificationBodies,
  countries: globals.countries,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadListCreator('certificationBodies')),
  setPage: (payload) => dispatch(setListPageCreator('certificationBodies', payload)),
  setPerPage: (payload) => dispatch(setListPerPageCreator('certificationBodies', payload)),
  setSort: (payload) => dispatch(setListSortCreator('certificationBodies', payload)),
  setFilters: (payload) => dispatch(setListFiltersCreator('certificationBodies', payload)),
  removeFilters: () => dispatch(removeListFiltersCreator('certificationBodies')),
  loadGlobalCountries: () => dispatch(loadGlobalCountriesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Certificates);
